import type { PlotRequest } from "@tento-data";
import { PlotCollection } from "@tento-data";

import type { CanvasRenderTarget } from "../canvas/canvas-types";
import type { CanvasSeriesRenderer } from "./renderer";
import { effectiveYExtent, toCanvasY } from "./scale-utils";

export type RuleOffsetType = "pct" | "abs";

export type HRuleRendererStyle = Readonly<{
  offset: number;
  offsetType: RuleOffsetType;
  center: Option<number>;
  strokeStyle: string;
  lineWidth: number;
}>;

const DEFAULT_HRULE_STYLE: HRuleRendererStyle = {
  offset: 0,
  offsetType: "pct",
  center: null,
  strokeStyle: "#d1d5db",
  lineWidth: 1,
};

/** Draws one horizontal reference rule against an entry's y-scale. */
export class HRuleRenderer implements CanvasSeriesRenderer {
  constructor(private readonly style: HRuleRendererStyle = DEFAULT_HRULE_STYLE) {}

  supports(entry: PlotCollection.AnyEntry): boolean {
    return supportsNumericY(entry);
  }

  render(
    target: CanvasRenderTarget,
    request: PlotRequest,
    entry: PlotCollection.AnyEntry,
  ): void {
    const extent = effectiveYExtent(entry, request);
    if (extent == null) return;
    const center = this.style.center ?? entry.descriptor.yRange?.center ??
      (extent.min + extent.max) / 2;
    const offset = canonicalOffset(
      this.style.offset,
      this.style.offsetType,
      extent.min,
      extent.max,
    );
    drawRule(target, extent, center + offset, this.style.strokeStyle, this.style.lineWidth);
  }
}

export type HRuleAllRendererStyle = HRuleRendererStyle;

/** Draws center plus symmetric upper/lower horizontal reference rules. */
export class HRuleAllRenderer implements CanvasSeriesRenderer {
  constructor(private readonly style: HRuleAllRendererStyle = {
    ...DEFAULT_HRULE_STYLE,
    offset: 0.1,
  }) {}

  supports(entry: PlotCollection.AnyEntry): boolean {
    return supportsNumericY(entry);
  }

  render(
    target: CanvasRenderTarget,
    request: PlotRequest,
    entry: PlotCollection.AnyEntry,
  ): void {
    const extent = effectiveYExtent(entry, request);
    if (extent == null) return;
    const center = this.style.center ?? entry.descriptor.yRange?.center ??
      (extent.min + extent.max) / 2;
    const offset = canonicalOffset(
      this.style.offset,
      this.style.offsetType,
      extent.min,
      extent.max,
    );

    drawRule(target, extent, center + offset, this.style.strokeStyle, this.style.lineWidth);
    drawRule(target, extent, center, this.style.strokeStyle, this.style.lineWidth);
    drawRule(target, extent, center - offset, this.style.strokeStyle, this.style.lineWidth);
  }
}

function supportsNumericY(entry: PlotCollection.AnyEntry): boolean {
  return PlotCollection.isScalarEntry(entry) ||
    PlotCollection.isBarWidthEntry(entry) ||
    PlotCollection.isRangeYEntry(entry) ||
    PlotCollection.isOhlcEntry(entry) ||
    PlotCollection.isPointEntry(entry);
}

export function canonicalOffset(
  offset: number,
  offsetType: RuleOffsetType,
  min: number,
  max: number,
): number {
  if (offsetType === "abs") return offset;
  return offset * (max - min);
}

function drawRule(
  target: CanvasRenderTarget,
  extent: { readonly min: number; readonly max: number },
  yValue: number,
  strokeStyle: string,
  lineWidth: number,
): void {
  const y = toCanvasY(yValue, extent, target.size.height);
  target.ctx.strokeStyle = strokeStyle;
  target.ctx.lineWidth = lineWidth;
  target.ctx.beginPath();
  target.ctx.moveTo(0, y);
  target.ctx.lineTo(target.size.width, y);
  target.ctx.stroke();
}
